import { getAuth } from 'firebase/auth';
import type {
  ProviderCredentialSaveResponse,
  ProviderCredentialsStatusResponse,
} from '../types';

function getBaseUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname === 'dev.segmently.ai' || hostname.includes('devsegmentlyai')) {
    return 'https://dev-api.segmently.ai';
  }
  if (hostname === 'stage.segmently.ai' || hostname.includes('stagesegmentlyai')) {
    return 'https://stage-api.segmently.ai';
  }
  return 'https://api.segmently.ai';
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuth().currentUser?.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handleResponse<T extends { success?: boolean; error?: string }>(
  response: Response,
): Promise<T> {
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text().catch(() => '');
  let data: { success?: boolean; error?: string } = {};

  if (contentType.includes('application/json') || text.trim().startsWith('{')) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON response from provider credentials API: HTTP ${response.status}`);
    }
  } else if (response.ok) {
    throw new Error('Provider credentials API returned a non-JSON response. Check VITE_BUILDER_API_URL.');
  }

  if (!response.ok || data.success === false) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }
  return data as T;
}

export class ProviderCredentialsApiService {
  constructor(private readonly baseUrl = getBaseUrl()) {}

  async getStatus(): Promise<ProviderCredentialsStatusResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/provider-credentials/status`, {
      headers: await getAuthHeaders(),
    });
    return handleResponse<ProviderCredentialsStatusResponse>(response);
  }

  async saveProvider(
    providerId: string,
    fields: Record<string, string>,
  ): Promise<ProviderCredentialSaveResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/admin/provider-credentials/${encodeURIComponent(providerId)}`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ fields }),
      },
    );
    return handleResponse<ProviderCredentialSaveResponse>(response);
  }

  async deleteField(
    providerId: string,
    fieldKey: string,
  ): Promise<ProviderCredentialSaveResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/admin/provider-credentials/${encodeURIComponent(providerId)}/${encodeURIComponent(fieldKey)}`,
      {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      },
    );
    return handleResponse<ProviderCredentialSaveResponse>(response);
  }
}

export function createProviderCredentialsApiService(baseUrl?: string): ProviderCredentialsApiService {
  return new ProviderCredentialsApiService(baseUrl);
}
